import React, { useEffect, useState } from 'react';

import { MuiBox, MuiTypography, MuiGrid, MuiContainer } from 'theme/material-ui';

import DiabetesBanner from 'assets/HealthTools/diabetes-web.png';

import { StyledScreen } from 'screens/HealthTools/Cards';
import Spacer from 'components/UI/Layout/Spacer';
import FlexBox from 'components/UI/Layout/FlexBox';
import {
  CardVideo,
  LanguageDropDown,
  PlayIcon,
  PrediabetesCardsVideos,
  VideoBanner
} from './styled';
import { prediabetesCardsVideosData, VideoTitle } from '../cardsVideos';

import { FontSize, FontWeight } from 'modules/styles/variables';
import { Color } from 'shared/src/modules/styles/colors';
import useNavigationAnalytics from 'hooks/useNavigationAnalytics';
import { AnalyticsEvent } from 'shared/src/services/AnalyticsService.common';

interface LanguageOption {
  label: string;
  value: 'es' | 'en';
}

const languagesOptions: LanguageOption[] = [
  {
    label: 'Spanish',
    value: 'es'
  },
  {
    label: 'English',
    value: 'en'
  }
];

const translations = {
  en: {
    imageTitle: 'Learn what you can do to prevent type 2 diabetes',
    paragraph:
      "Watch the videos below to learn more about healthy lifestyle choices and decisions that decrease your risk for type 2 diabetes. These videos cover the same information shared in Intermountain's prediabetes 101 class."
  },
  es: {
    imageTitle: 'Aprende que puedes hace para prevenir la diabetes',
    paragraph:
      'Vea los videos a continuación para aprender más sobre alternativas para un estilo de vida saludable y decisiones que puede tomar para disminuir su riesgo de contraer la diabetes tipo 2. Estos videos cubren la misma información que se comparte en la clase de Intermountain para la prediabetes 101.'
  }
};

export default function PreventDiabetesScreen() {
  const [currentLanguage, setCurrentLanguage] = useState<'es' | 'en'>(languagesOptions[1].value);
  const [clickedVideoTitle, setClickedVideoTitle] = useState<string>('');
  const [changedLanguage, setChangedLanguage] = useState<boolean>(false);
  const preventDiabetesVideoSelected = useNavigationAnalytics(
    AnalyticsEvent.PreventPrediabetesVideoSelected,
    {
      title: 'Prediabetes Landing Page',
      video: clickedVideoTitle
    }
  );
  const preventLanguageSelected = useNavigationAnalytics(
    AnalyticsEvent.PreventDiabetesLanguageSelected,
    {
      title: 'Prediabetes Landing Page',
      language: currentLanguage === 'es' ? 'Spanish' : 'English'
    }
  );

  useEffect(() => {
    if (clickedVideoTitle && clickedVideoTitle.length) {
      preventDiabetesVideoSelected();
      setClickedVideoTitle('');
    }
  }, [clickedVideoTitle]);

  useEffect(() => {
    if (changedLanguage) {
      preventLanguageSelected();
    }
  }, [currentLanguage]);

  const onChangeLanguage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChangedLanguage(true);
    setCurrentLanguage(e.target.value as 'es' | 'en');
  };

  const openVideo = (title: VideoTitle) => {
    if (!title || !title[currentLanguage]) {
      return;
    }

    setClickedVideoTitle(title[currentLanguage] as string);
  };

  return (
    <StyledScreen>
      <MuiBox pl={13} pr={13} my={3} display="center" data-testid="Vynca">
        <MuiGrid container style={{ backgroundColor: Color.white, display: 'flex' }}>
          <MuiGrid item style={{ display: 'flex' }} xs={6}>
            <img src={DiabetesBanner} alt="VyncaBanner" height="auto" width="100%"></img>
          </MuiGrid>
          <MuiGrid item xs={6} style={{ display: 'flex' }} alignItems="center">
            <MuiBox pl={2}>
              <MuiTypography
                fontSize={FontSize.large}
                fontWeight={FontWeight.bold}
                color={Color.blueHue9}
                data-testid="section-description"
              >
                {translations[currentLanguage].imageTitle}
              </MuiTypography>
            </MuiBox>
          </MuiGrid>
        </MuiGrid>

        <Spacer size="medium" />

        <MuiContainer maxWidth="lg">
          <FlexBox alignItems="center">
            <MuiTypography>
              <MuiTypography fontSize={FontSize.large} fontWeight={FontWeight.regularbold}>
                {translations[currentLanguage].paragraph}
              </MuiTypography>
            </MuiTypography>
          </FlexBox>
        </MuiContainer>

        <MuiContainer maxWidth="xl">
          <FlexBox alignItems="normal">
            <MuiTypography>
              <Spacer size="medium" />
              <MuiTypography fontSize={FontSize.large} fontWeight={FontWeight.regularbold}>
                Language / Idioma
              </MuiTypography>
            </MuiTypography>

            <MuiTypography>
              <LanguageDropDown
                items={languagesOptions}
                onChange={onChangeLanguage}
                value={currentLanguage}
              />
            </MuiTypography>
          </FlexBox>

          <Spacer size="medium" />

          <PrediabetesCardsVideos>
            {prediabetesCardsVideosData
              .filter(card => card.title[currentLanguage])
              .map(card => {
                return (
                  <CardVideo>
                    <img
                      alt={`Prediabetes video ${card.title[currentLanguage]}`}
                      src={card.thumb}
                    />

                    <VideoBanner>
                      <p>{card.title[currentLanguage]}</p>
                      <PlayIcon onClick={() => openVideo(card.title)} />
                    </VideoBanner>
                  </CardVideo>
                );
              })}
          </PrediabetesCardsVideos>
        </MuiContainer>
      </MuiBox>
    </StyledScreen>
  );
}
